/**
 * AcpProviderInstance — ACP (Agent Client Protocol) Provider runtime instance
 *
 * Spawns ACP agent process and communicates via the official ACP SDK.
 * Uses ClientSideConnection + ndJsonStream for structured protocol communication.
 *
 * ACP spec: https://agentclientprotocol.com
 * ACP SDK: @agentclientprotocol/sdk@0.16.1
 * 
 * lifecycle:
 * 1. init() → Spawn agent process + ACP initialize handshake
 * 2. onTick() → no-op (ACP event based)
 * 3. getState() → ProviderState return (dashboard for display)
 * 4. onEvent('send_message') → session/prompt transmit
 * 5. dispose() → kill process
 */

import * as path from 'path';
import { Readable, Writable } from 'stream';
import { spawn, type ChildProcess } from 'child_process';
import {
    ClientSideConnection,
    ndJsonStream,
    RequestError,
    PROTOCOL_VERSION,
    type Client,
    type Agent,
    type SessionNotification,
    type RequestPermissionRequest,
    type RequestPermissionResponse,
    type WriteTextFileRequest,
    type WriteTextFileResponse,
    type ReadTextFileRequest,
    type ReadTextFileResponse,
    type CreateTerminalRequest,
    type CreateTerminalResponse,
    type TerminalOutputRequest,
    type TerminalOutputResponse,
    type ReleaseTerminalRequest,
    type ReleaseTerminalResponse,
    type WaitForTerminalExitRequest,
    type WaitForTerminalExitResponse,
    type KillTerminalRequest,
    type KillTerminalResponse,
    type SessionUpdate,
    type ToolCallStatus,
    type SessionConfigOption,
} from '@agentclientprotocol/sdk';
import type { ProviderModule, ContentBlock, InputEnvelope, ToolCallInfo, ToolCallContent as TCC, ToolKind, ToolCallStatus as TCS } from './contracts.js';
import { normalizeContent, flattenContent, normalizeInputEnvelope } from './contracts.js';
import { assertProviderSupportsDeclaredInput, getEffectiveMessageInputSupport } from './provider-input-support.js';
import type { ProviderInstance, ProviderState, AcpProviderState, ProviderErrorReason, ProviderEvent, InstanceContext, SessionModalState } from './provider-instance.js';
import { StatusMonitor } from './status-monitor.js';
import { ManualAttendanceTracker } from './manual-attendance.js';
import { buildLegacyModelModeSummaryMetadata } from './summary-metadata.js';
import { workingDirBasename } from './working-dir.js';
import {
    buildAssistantChatMessage,
    buildChatMessage,
    buildRuntimeSystemChatMessage,
    buildTerminalChatMessage,
    buildThoughtChatMessage,
    buildToolChatMessage,
    buildUserChatMessage,
    normalizeChatMessages,
    extractFinalSummaryFromMessages,
} from './chat-message-normalization.js';
import { LOG } from '../logging/logger.js';
import type { ChatMessage } from '../types.js';

// ─── Internal Display Types (for dashboard) ────────────────────────────

type AcpMessage = ChatMessage & {
    role: 'user' | 'assistant' | 'system';
    /** Rich content blocks (ACP standard) or plain text (legacy) */
    content: string | ContentBlock[];
    /** Tool calls associated with this message */
    toolCalls?: ToolCallInfo[];
}

interface AcpToolCall {
    id: string;
    name: string;
    status: 'running' | 'completed' | 'failed';
    input?: string;
    output?: string;
}

interface AcpConfigOption {
    category: 'model' | 'mode' | 'thought_level' | 'other';
    configId: string;
    currentValue?: string;
    options: { value: string; name: string; description?: string; group?: string }[];
}

interface AcpMode {
    id: string;
    name: string;
    description?: string;
}

type SelectionCategory = 'model' | 'mode';

interface PromptCapabilityFlags {
    image: boolean;
    audio: boolean;
    embeddedContext: boolean;
}

function getPromptCapabilityFlags(agentCapabilities?: Record<string, any>): PromptCapabilityFlags {
    const prompt = agentCapabilities?.promptCapabilities || {};
    return {
        image: prompt.image === true,
        audio: prompt.audio === true,
        embeddedContext: prompt.embeddedContext === true,
    };
}

function appendPromptText(promptParts: ContentBlock[], text: string | undefined): void {
    const normalized = typeof text === 'string' ? text.trim() : '';
    if (!normalized) return;
    const last = promptParts[promptParts.length - 1];
    if (last?.type === 'text' && last.text === normalized) return;
    promptParts.push({ type: 'text', text: normalized });
}

function getUriDisplayName(uri: string | undefined, fallback: string): string {
    if (!uri) return fallback;
    try {
        const pathname = uri.startsWith('file://') ? new URL(uri).pathname : uri;
        return pathname.split(/[\\/]/).filter(Boolean).pop() || fallback;
    } catch {
        return uri.split(/[\\/]/).filter(Boolean).pop() || fallback;
    }
}

function appendResourceLink(
    promptParts: ContentBlock[],
    uri: string,
    fallbackName: string,
    mimeType?: string,
    description?: string,
    metadata?: Pick<Extract<ContentBlock, { type: 'resource_link' }>, 'title' | 'size' | 'annotations'> & { name?: string },
): void {
    promptParts.push({
        type: 'resource_link',
        uri,
        name: metadata?.name || getUriDisplayName(uri, fallbackName),
        ...(metadata?.title ? { title: metadata.title } : {}),
        ...(mimeType ? { mimeType } : {}),
        ...(description ? { description } : {}),
        ...(typeof metadata?.size === 'number' ? { size: metadata.size } : {}),
        ...(metadata?.annotations ? { annotations: metadata.annotations } : {}),
    });
}

function appendMediaFallbackText(promptParts: ContentBlock[], label: string, details: Array<string | undefined>): void {
    const normalizedDetails = details.map((value) => typeof value === 'string' ? value.trim() : '').filter(Boolean);
    appendPromptText(promptParts, `[${[label, ...normalizedDetails].join(': ')}]`);
}

export function buildAcpPromptParts(input: InputEnvelope, agentCapabilities?: Record<string, any>): ContentBlock[] {
    const caps = getPromptCapabilityFlags(agentCapabilities);
    const promptParts: ContentBlock[] = [];

    for (const part of input.parts) {
        if (part.type === 'text') {
            promptParts.push({ type: 'text', text: part.text });
            continue;
        }

        if (part.type === 'image') {
            if (caps.image && part.data) {
                promptParts.push({
                    type: 'image',
                    data: part.data,
                    mimeType: part.mimeType,
                    ...(part.uri ? { uri: part.uri } : {}),
                    ...(part.alt ? { alt: part.alt } : {}),
                });
                if (part.alt) appendPromptText(promptParts, part.alt);
            } else if (part.uri) {
                appendResourceLink(promptParts, part.uri, 'image', part.mimeType, part.alt);
                if (part.alt) appendPromptText(promptParts, part.alt);
            } else {
                appendMediaFallbackText(promptParts, 'Image attachment', [part.alt, part.mimeType]);
            }
            continue;
        }

        if (part.type === 'audio') {
            if (caps.audio && part.data) {
                promptParts.push({
                    type: 'audio',
                    data: part.data,
                    mimeType: part.mimeType,
                    ...(part.uri ? { uri: part.uri } : {}),
                    ...(part.transcript ? { transcript: part.transcript } : {}),
                });
                if (part.transcript) appendPromptText(promptParts, part.transcript);
            } else if (part.uri) {
                appendResourceLink(promptParts, part.uri, 'audio', part.mimeType, part.transcript);
                if (part.transcript) appendPromptText(promptParts, part.transcript);
            } else {
                appendMediaFallbackText(promptParts, 'Audio attachment', [part.transcript, part.mimeType]);
            }
            continue;
        }

        if (part.type === 'resource') {
            if (caps.embeddedContext && part.text) {
                promptParts.push({
                    type: 'resource',
                    resource: { uri: part.uri, text: part.text, mimeType: part.mimeType ?? null },
                });
                continue;
            }
            if (caps.embeddedContext && part.data) {
                promptParts.push({
                    type: 'resource',
                    resource: { uri: part.uri, blob: part.data, mimeType: part.mimeType ?? null },
                });
                continue;
            }
            appendResourceLink(promptParts, part.uri, part.name || 'resource', part.mimeType, part.text);
            if (part.text) appendPromptText(promptParts, part.text);
            continue;
        }

        if (part.type === 'resource_link') {
            appendResourceLink(promptParts, part.uri, part.name, part.mimeType, part.description, {
                name: part.name,
                ...(part.title ? { title: part.title } : {}),
                ...(typeof part.size === 'number' ? { size: part.size } : {}),
                ...(part.annotations ? { annotations: part.annotations } : {}),
            });
            continue;
        }

        if (part.type === 'video') {
            // ACP v0.16 prompt capabilities do not advertise native video input. Preserve meaning by
            // sending a linked resource when possible, plus transcript/descriptive text when present.
            if (part.uri) {
                appendResourceLink(promptParts, part.uri, 'video', part.mimeType, part.transcript);
                if (part.transcript) appendPromptText(promptParts, part.transcript);
            } else {
                appendMediaFallbackText(promptParts, 'Video attachment', [part.transcript, part.mimeType]);
            }
        }
    }

    if (!promptParts.some((part) => part.type === 'text') && input.textFallback) {
        appendPromptText(promptParts, input.textFallback);
    }

    return promptParts;
}

// ─── AcpProviderInstance ───────────────────────────

export class AcpProviderInstance implements ProviderInstance {
    readonly type: string;
    readonly category = 'acp' as const;
    private readonly log = LOG.forComponent('ACP');

    private provider: ProviderModule;
    private context: InstanceContext | null = null;
    private settings: Record<string, any> = {};
    private events: ProviderEvent[] = [];
    private monitor: StatusMonitor;

 // Process
    private process: ChildProcess | null = null;
    private connection: ClientSideConnection | null = null;

 // State
    private sessionId: string | null = null;
    private messages: AcpMessage[] = [];
    private currentStatus: ProviderState['status'] = 'starting';
    private lastStatus: string = 'starting';
    private generatingStartedAt = 0;
    private agentCapabilities: Record<string, any> = {};
    private currentSelections: Partial<Record<SelectionCategory, string>> = {};
    private activeToolCalls: AcpToolCall[] = [];
    private stopReason: string | null = null;
    private partialContent = '';
    private partialThoughtContent = '';
    /** Rich content blocks accumulated during streaming */
    private partialBlocks: ContentBlock[] = [];
    /** Tool calls collected during current turn */
    private turnToolCalls: ToolCallInfo[] = [];
 /** Guard: prevent concurrent sendPrompt calls from racing on shared state */
    private _sendPromptInFlight = false;

 // Error tracking
    private errorMessage: string | null = null;
    private errorReason: ProviderErrorReason | null = null;
    private stderrBuffer: string[] = [];
    private spawnedAt = 0;

 // ACP ConfigOptions & Modes (from session/new response or static fallback)
    private configOptions: AcpConfigOption[] = [];
    private availableModes: AcpMode[] = [];
 /** Static config mode — agent doesn't support config/* methods */
    private useStaticConfig = false;
 /** Current config selections (for spawnArgBuilder) */
    private selectedConfig: Record<string, string> = {};

 // Config
    private workingDir: string;
    private instanceId: string;

    constructor(
        provider: ProviderModule,
        workingDir: string,
        private cliArgs: string[] = [],
    ) {
        this.type = provider.type;
        this.provider = provider;
        this.workingDir = workingDir;
        this.instanceId = crypto.randomUUID();

        this.monitor = new StatusMonitor();
    }

 // ─── Lifecycle ─────────────────────────────────

    async init(context: InstanceContext): Promise<void> {
        this.context = context;
        this.settings = context.settings || {};
        this.monitor.updateConfig({
            approvalAlert: this.settings.approvalAlert !== false,
            noProgressAlert: (this.settings.noProgressAlert ?? this.settings.longGeneratingAlert) !== false,
            noProgressThresholdSec: this.settings.noProgressThresholdSec ?? this.settings.longGeneratingThresholdSec ?? 180,
        });

        await this.spawnAgent();
    }

    async onTick(): Promise<void> {
 // ACP event based — tick unnecessary
 // Run process health check only
        if (this.process && this.process.exitCode !== null) {
            this.currentStatus = 'stopped';
            this.detectStatusTransition();
        }
    }

    getSessionModalState(): SessionModalState {
        const dirName = workingDirBasename(this.workingDir);
        return {
            id: this.instanceId,
            status: this.currentStatus,
            title: `${this.provider.name} · ${dirName}`,
            activeModal: this.currentStatus === 'waiting_approval' ? {
                message: this.activeToolCalls.find(t => t.status === 'running')?.name || 'Permission requested',
                buttons: ['Approve', 'Reject'],
            } : null,
        };
    }

    getState(): AcpProviderState {
        const dirName = workingDirBasename(this.workingDir);

        const recentMessages = normalizeChatMessages(this.messages.map(m => {
            const content = m.content;
            return buildChatMessage({
                ...m,
                content,
            });
        })) as ChatMessage[];

        if (this.currentStatus === 'generating') {
            const partialThoughtMessage = this.buildPartialThoughtMessage(Date.now());
            if (partialThoughtMessage) recentMessages.push(partialThoughtMessage as ChatMessage);
        }

 // generating during partial response add
        if (this.currentStatus === 'generating' && (this.partialContent || this.partialBlocks.length > 0)) {
            const blocks = this.buildPartialBlocks();
            if (blocks.length > 0) {
                recentMessages.push(buildAssistantChatMessage({
                    content: blocks,
                    timestamp: Date.now(),
                    toolCalls: this.turnToolCalls.length > 0 ? [...this.turnToolCalls] : undefined,
                }));
            }
        }

        return {
            type: this.type,
            name: this.provider.name,
            category: 'acp',
            status: this.currentStatus,
            mode: 'chat',
            activeChat: {
                id: this.sessionId || `${this.type}_${this.workingDir}`,
                title: `${this.provider.name} · ${dirName}`,
                status: this.currentStatus,
                messages: normalizeChatMessages(recentMessages as any),
                activeModal: this.currentStatus === 'waiting_approval' ? {
                    message: this.activeToolCalls.find(t => t.status === 'running')?.name || 'Permission requested',
                    buttons: ['Approve', 'Reject'],
                } : null,
                inputContent: '',
            },
            workspace: this.workingDir,
            instanceId: this.instanceId,
            lastUpdated: Date.now(),
            settings: this.settings,
            pendingEvents: this.flushEvents(),
            messageInput: getEffectiveMessageInputSupport(this.provider, this.agentCapabilities),
 // ACP-specific: expose available models/modes for dashboard
            acpConfigOptions: this.configOptions,
            acpModes: this.availableModes,
 // Error details for dashboard display
            errorMessage: this.errorMessage || undefined,
            errorReason: this.errorReason || undefined,
            controlValues: this.getSelectionControlValues(),
            providerControls: this.provider.controls,
            summaryMetadata: this.buildSelectionSummaryMetadata(),
        };
    }

    onEvent(event: string, data?: any): void {
        if (event === 'send_message') {
            const input = normalizeInputEnvelope(data)
            assertProviderSupportsDeclaredInput(this.provider, input)
            const promptParts = buildAcpPromptParts(input, this.agentCapabilities)
            this.sendPrompt(input.textFallback, promptParts.length > 0 ? promptParts : undefined).catch(e =>
                this.log.warn(`[${this.type}] sendPrompt error: ${e?.message}`)
            );
        } else if (event === 'resolve_action') {
            const action = data?.action || 'approve';
            this.resolvePermission(action === 'approve' || action === 'accept')
                .catch(e => this.log.warn(`[${this.type}] resolvePermission error: ${e?.message}`));
        } else if (event === 'cancel') {
            this.cancelSession().catch(e =>
                this.log.warn(`[${this.type}] cancel error: ${e?.message}`)
            );
        } else if (event === 'change_model' && data?.model) {
            this.setConfigOption('model', data.model).catch(e =>
                this.log.warn(`[${this.type}] change_model error: ${e?.message}`)
            );
        } else if (event === 'set_mode' && data?.mode) {
            this.setMode(data.mode).catch(e =>
                this.log.warn(`[${this.type}] set_mode error: ${e?.message}`)
            );
        } else if (event === 'set_thought_level' && data?.level) {
            this.setConfigOption('thought_level', data.level).catch(e =>
                this.log.warn(`[${this.type}] set_thought_level error: ${e?.message}`)
            );
        }
    }

    getInstanceId(): string {
        return this.instanceId;
    }

    private resolveConfigOptionLabel(category: string, value: string | undefined): string | undefined {
        if (!value) return undefined;
        const option = this.configOptions.find((entry) => entry.category === category);
        return option?.options.find((candidate) => candidate.value === value)?.name || value;
    }

    private resolveModeLabel(modeId: string | undefined): string | undefined {
        if (!modeId) return undefined;
        return this.availableModes.find((mode) => mode.id === modeId)?.name || modeId;
    }

    private getCurrentSelection(category: SelectionCategory): string | undefined {
        return this.currentSelections[category];
    }

    private setCurrentSelection(category: SelectionCategory, value: string | null | undefined): void {
        const normalized = typeof value === 'string' ? value.trim() : '';
        if (normalized) {
            this.currentSelections[category] = normalized;
            return;
        }
        delete this.currentSelections[category];
    }

    private getSelectionControlValues(): Record<string, string> {
        const model = this.getCurrentSelection('model');
        const mode = this.getCurrentSelection('mode');
        return {
            ...(model ? { model } : {}),
            ...(mode ? { mode } : {}),
        };
    }

    private resolveSelectionLabel(category: SelectionCategory, value: string | undefined): string | undefined {
        if (!value) return undefined;
        const configLabel = this.resolveConfigOptionLabel(category, value);
        if (configLabel && configLabel !== value) return configLabel;
        if (category === 'mode') {
            const modeLabel = this.resolveModeLabel(value);
            if (modeLabel) return modeLabel;
        }
        return configLabel || value;
    }

    private buildSelectionSummaryMetadata() {
        const model = this.getCurrentSelection('model');
        const mode = this.getCurrentSelection('mode');
        return buildLegacyModelModeSummaryMetadata({
            model,
            mode,
            modelLabel: this.resolveSelectionLabel('model', model),
            modeLabel: this.resolveSelectionLabel('mode', mode),
        });
    }

 // ─── ACP Config Options & Modes ─────────────────────

    private parseConfigOptions(raw: any): void {
        if (!Array.isArray(raw)) return;
        this.configOptions = [];
        for (const opt of raw) {
            const category = opt.category || 'other';
            const configId = opt.configId || opt.id || '';
            const currentValue = opt.currentValue ?? opt.select?.currentValue;

 // flatten options (ungrouped + grouped)
            const flatOptions: AcpConfigOption['options'] = [];
            const selectOpts = opt.select?.options || opt.options;
            if (selectOpts) {
 // ungrouped options
                if (Array.isArray(selectOpts.ungrouped)) {
                    for (const o of selectOpts.ungrouped) {
                        flatOptions.push({ value: o.value, name: o.name || o.value, description: o.description });
                    }
                }
 // grouped options
                if (Array.isArray(selectOpts.grouped)) {
                    for (const g of selectOpts.grouped) {
                        const groupName = g.name || g.group || '';
                        for (const o of (Array.isArray(g.options?.ungrouped) ? g.options.ungrouped : (g.options || []))) {
                            flatOptions.push({ value: o.value, name: o.name || o.value, description: o.description, group: groupName });
                        }
                    }
                }
 // direct array
                if (Array.isArray(selectOpts)) {
                    for (const o of selectOpts) {
                        if (o.value) flatOptions.push({ value: o.value, name: o.name || o.value, description: o.description });
                    }
                }
            }

            this.configOptions.push({ category: category as 'model' | 'mode' | 'thought_level' | 'other', configId, currentValue, options: flatOptions });

 // Auto-set current selections from config
            if (category === 'model' || category === 'mode') {
                this.setCurrentSelection(category, currentValue);
            }
        }
    }

    private parseModes(raw: any): void {
        if (!raw) return;
 // modes: { currentModeId, availableModes: [{ id, name, description }] }
        this.setCurrentSelection('mode', raw.currentModeId);
        if (Array.isArray(raw.availableModes)) {
            this.availableModes = raw.availableModes.map((m: any) => ({
                id: m.id, name: m.name || m.id, description: m.description,
            }));
        }
    }

    async setConfigOption(category: string, value: string): Promise<void> {
 // Find configId for this category
        const opt = this.configOptions.find(c => c.category === category);
        if (!opt) {
            const message = `[${this.type}] No config option for category: ${category}`;
            this.log.warn(message);
            throw new Error(message);
        }

 // Static config mode: update selection and restart process
        if (this.useStaticConfig) {
            opt.currentValue = value;
            this.selectedConfig[opt.configId] = value;
            if (category === 'model' || category === 'mode') this.setCurrentSelection(category, value);
            this.log.info(`[${this.type}] Static config ${category} set to: ${value} — restarting agent`);
            await this.restartWithNewConfig();
            return;
        }

        if (!this.connection || !this.sessionId) {
            const message = `[${this.type}] Cannot set config: no active connection/session`;
            this.log.warn(message);
            throw new Error(message);
        }

        try {
            this.log.info(`[${this.type}] Sending session/set_config_option: configId=${opt.configId} value=${value} sessionId=${this.sessionId}`);
            const result = await this.connection.setSessionConfigOption({
                sessionId: this.sessionId,
                configId: opt.configId,
                value,
            });
 // Update local state
            opt.currentValue = value;
            if (category === 'model' || category === 'mode') this.setCurrentSelection(category, value);
 // Response may include updated configOptions
            if (result?.configOptions) this.parseConfigOptions(result.configOptions);
            this.log.info(`[${this.type}] Config ${category} set to: ${value} | response: ${JSON.stringify(result)?.slice(0, 300)}`);
        } catch (e: any) {
            const message = e?.message || 'Unknown ACP config error';
            this.log.warn(`[${this.type}] set_config_option failed: ${message}`);
            throw new Error(message);
        }
    }

    async setMode(modeId: string): Promise<void> {
 // Static config: mode changes via restart
        if (this.useStaticConfig) {
            const opt = this.configOptions.find(c => c.category === 'mode');
            if (opt) {
                opt.currentValue = modeId;
                this.selectedConfig[opt.configId] = modeId;
            }
            this.setCurrentSelection('mode', modeId);
            this.log.info(`[${this.type}] Static mode set to: ${modeId} — restarting agent`);
            await this.restartWithNewConfig();
            return;
        }

        if (!this.connection || !this.sessionId) {
            const message = `[${this.type}] Cannot set mode: no active connection/session`;
            this.log.warn(message);
            throw new Error(message);
        }

        try {
            await this.connection.setSessionMode({
                sessionId: this.sessionId,
                modeId,
            });
            this.setCurrentSelection('mode', modeId);
            this.log.info(`[${this.type}] Mode set to: ${modeId}`);
        } catch (e: any) {
            const message = e?.message || 'Unknown ACP mode error';
            this.log.warn(`[${this.type}] set_mode failed: ${message}`);
            throw new Error(message);
        }
    }

 /** Static config: kill process and restart with new args */
    private async restartWithNewConfig(): Promise<void> {
 // Build new args from spawnArgBuilder
        if (this.provider.spawnArgBuilder) {
            this.cliArgs = []; // clear previous extra args
        }

 // Kill existing process
        if (this.process) {
            try { this.process.kill('SIGTERM'); } catch { }
            this.process = null;
        }
        this.connection = null;
        this.sessionId = null;

        this.currentStatus = 'starting';
        this.detectStatusTransition();

 // Re-spawn with updated config
        await this.spawnAgent();
    }

    /** Update settings at runtime (called when user changes settings from dashboard) */
    updateSettings(newSettings: Record<string, any>): void {
        this.settings = { ...this.settings, ...newSettings };
        this.monitor.updateConfig({
            approvalAlert: this.settings.approvalAlert !== false,
            noProgressAlert: (this.settings.noProgressAlert ?? this.settings.longGeneratingAlert) !== false,
            noProgressThresholdSec: this.settings.noProgressThresholdSec ?? this.settings.longGeneratingThresholdSec ?? 180,
        });
        this.log.info(`[${this.type}] Settings updated: ${Object.keys(newSettings).join(', ')}`);
    }

    dispose(): void {
        // kill process
        if (this.process) {
            try { this.process.kill('SIGTERM'); } catch { }
            this.process = null;
        }
        this.connection = null;
        this.monitor.reset();
    }

 // ─── ACP Process Management ──────────────────────

    private async spawnAgent(): Promise<void> {
        const spawnConfig = this.provider.spawn;
        if (!spawnConfig) {
            throw new Error(`[ACP:${this.type}] No spawn config defined`);
        }

        const command = typeof this.settings.executablePath === 'string' && this.settings.executablePath.trim()
            ? this.settings.executablePath.trim()
            : spawnConfig.command;
 // Static config: create args via spawnArgBuilder (when provider defines it)
        let baseArgs = spawnConfig.args || [];
        if (this.provider.spawnArgBuilder && Object.keys(this.selectedConfig).length > 0) {
            baseArgs = this.provider.spawnArgBuilder(this.selectedConfig);
        }
        const args = [...baseArgs, ...this.cliArgs];

 // Auth: each CLI/ACP tool manages its own authentication.
 // ADHDev does NOT inject API keys — tools read their own env vars or config files.

        const env = { ...process.env, ...(spawnConfig.env || {}) };

        this.log.info(`[${this.type}] Spawning: ${command} ${args.join(' ')} in ${this.workingDir}`);

        this.spawnedAt = Date.now();
        this.errorMessage = null;
        this.errorReason = null;
        this.stderrBuffer = [];

        this.process = spawn(command, args, {
            cwd: this.workingDir,
            env,
            stdio: ['pipe', 'pipe', 'pipe'],
            shell: spawnConfig.shell || false,
            ...(process.platform === 'win32' ? { windowsHide: true } : {}),
        });

 // stderr → log + auth failure detection
        const AUTH_ERROR_PATTERNS = [
            /unauthorized|unauthenticated/i,
            /invalid.*(?:api[_ ]?key|token|credential)/i,
            /auth(?:entication|orization).*(?:fail|error|denied|invalid|expired)/i,
            /(?:api[_ ]?key|token).*(?:missing|required|not set|not found|invalid|expired)/i,
            /ENOENT|command not found|not recognized/i,
            /permission denied/i,
            /rate.?limit|quota.?exceeded/i,
            /login.*required|please.*(?:login|authenticate|sign.?in)/i,
        ];

        this.process.stderr?.on('data', (data) => {
            const text = data.toString().trim();
            if (!text) return;
            this.log.debug(`[${this.type}:stderr] ${text.slice(0, 300)}`);

 // Maintain stderr buffer (recent 20 lines)
            this.stderrBuffer.push(text);
            if (this.stderrBuffer.length > 20) this.stderrBuffer.shift();

 // Auth failure detection
            for (const pattern of AUTH_ERROR_PATTERNS) {
                if (pattern.test(text)) {
                    if (/ENOENT|command not found|not recognized/i.test(text)) {
                        this.errorReason = 'not_installed';
                        this.errorMessage = `Command '${command}' not found. Install: ${this.provider.install || 'check documentation'}`;
                    } else {
                        this.errorReason = 'auth_failed';
                        this.errorMessage = text.slice(0, 300);
                    }
                    this.log.warn(`[${this.type}] Error detected (${this.errorReason}): ${this.errorMessage?.slice(0, 100)}`);
                    break;
                }
            }
        });

 // kill process detect
        this.process.on('exit', (code, signal) => {
            const elapsed = Date.now() - this.spawnedAt;
            this.log.info(`[${this.type}] Process exited: code=${code} signal=${signal} elapsed=${elapsed}ms`);

 // Exit code analysis
            if (code !== 0 && code !== null) {
                if (!this.errorReason) {
                    if (code === 127) {
                        this.errorReason = 'not_installed';
                        this.errorMessage = `Command '${command}' not found (exit code 127). Install: ${this.provider.install || 'check documentation'}`;
                    } else if (elapsed < 3000) {
 // 3-second crash → likely install/auth issue
                        this.errorReason = this.stderrBuffer.length > 0 ? 'crash' : 'spawn_error';
                        this.errorMessage = this.stderrBuffer.length > 0
                            ? `Agent crashed immediately (exit code ${code}): ${this.stderrBuffer.slice(-3).join(' | ').slice(0, 300)}`
                            : `Agent exited immediately with code ${code}. The agent may not be installed correctly.`;
                    } else {
                        this.errorReason = 'crash';
                        this.errorMessage = `Agent exited with code ${code}${this.stderrBuffer.length > 0 ? ': ' + this.stderrBuffer.slice(-1)[0]?.slice(0, 200) : ''}`;
                    }
                }
            }

            this.currentStatus = this.errorReason ? 'error' : 'stopped';
            this.detectStatusTransition();
        });

        this.process.on('error', (err) => {
            this.log.error(`[${this.type}] Process spawn error: ${err.message}`);
            if (err.message.includes('ENOENT')) {
                this.errorReason = 'not_installed';
                this.errorMessage = `Command '${command}' not found. Install: ${this.provider.install || 'check documentation'}`;
            } else {
                this.errorReason = 'spawn_error';
                this.errorMessage = err.message;
            }
            this.currentStatus = 'error';
            this.detectStatusTransition();
        });

 // ─── SDK Connection Setup ────────────────────────
 // Convert Node.js streams to Web Streams for ndJsonStream
        const webStdin = Writable.toWeb(this.process.stdin!) as WritableStream<Uint8Array>;
        const webStdout = Readable.toWeb(this.process.stdout!) as ReadableStream<Uint8Array>;
        const stream = ndJsonStream(webStdin, webStdout);

 // Create ClientSideConnection with our Client implementation
        this.connection = new ClientSideConnection((_agent: Agent) => this.createClient(), stream);

 // Listen for connection close
        this.connection.signal.addEventListener('abort', () => {
            this.log.info(`[${this.type}] ACP connection closed`);
        });

 // ACP initialize handshake
        await this.initialize();
    }

 // ─── Client Interface Implementation ────────────────────

    private createClient(): Client {
        return {
            requestPermission: async (params: RequestPermissionRequest): Promise<RequestPermissionResponse> => {
                // Update active tool calls from the request
                const tc = params.toolCall;
                const existing = this.activeToolCalls.find(t => t.id === tc.toolCallId);
                if (existing) {
                    existing.status = 'running';
                    if (tc.title) existing.name = tc.title;
                } else {
                    this.activeToolCalls.push({
                        id: tc.toolCallId,
                        name: tc.title || 'unknown',
                        status: 'running',
                        input: tc.rawInput ? (typeof tc.rawInput === 'string' ? tc.rawInput : JSON.stringify(tc.rawInput)) : undefined,
                    });
                }

                // ─── Auto-approve: skip user confirmation ───
                // Held while a human is actively attending this session (manual
                // attendance) so they can decide the permission themselves; falls
                // through to the waiting_approval manual path below. A background
                // worker is never attended, so its delegated auto-approve fires
                // as before.
                if (this.settings.autoApprove !== false && !this.manualAttendance.isAttended()) {
                    const toolTitle = tc.title || tc.toolCallId || 'tool call';
                    this.log.info(`[${this.type}] Auto-approving: ${toolTitle}`);
                    this.appendSystemMessage(`Auto-approved: ${toolTitle}`);
                    const allowOption = params.options.find(o => o.kind === 'allow_once') || params.options.find(o => o.kind === 'allow_always');
                    if (allowOption) {
                        return { outcome: { outcome: 'selected', optionId: allowOption.optionId } };
                    }
                    return { outcome: { outcome: 'selected', optionId: params.options[0]?.optionId || '' } };
                }

                // Approval request → switch to waiting_approval status
                this.currentStatus = 'waiting_approval';
                this.detectStatusTransition();

                // Wait for user approval
                const approved = await new Promise<boolean>((resolve) => {
                    this.permissionResolvers.push(resolve);
                    // 5-minute timeout → auto-reject
                    setTimeout(() => {
                        const idx = this.permissionResolvers.indexOf(resolve);
                        if (idx >= 0) {
                            this.permissionResolvers.splice(idx, 1);
                            resolve(false);
                        }
                    }, 300_000);
                });

                if (approved) {
 // Find the "allow" option (allow_once or allow_always)
                    const allowOption = params.options.find(o => o.kind === 'allow_once') || params.options.find(o => o.kind === 'allow_always');
                    if (allowOption) {
                        return { outcome: { outcome: 'selected', optionId: allowOption.optionId } };
                    }
 // Fallback: use first option
                    return { outcome: { outcome: 'selected', optionId: params.options[0]?.optionId || '' } };
                } else {
 // Find the "reject" option
                    const rejectOption = params.options.find(o => o.kind === 'reject_once') || params.options.find(o => o.kind === 'reject_always');
                    if (rejectOption) {
                        return { outcome: { outcome: 'selected', optionId: rejectOption.optionId } };
                    }
                    return { outcome: { outcome: 'cancelled' } };
                }
            },

            sessionUpdate: async (params: SessionNotification): Promise<void> => {
                this.handleSessionUpdate(params);
            },

 // File system — not supported
            readTextFile: async (_params: ReadTextFileRequest): Promise<ReadTextFileResponse> => {
                throw RequestError.methodNotFound('fs/read_text_file');
            },
            writeTextFile: async (_params: WriteTextFileRequest): Promise<WriteTextFileResponse> => {
                throw RequestError.methodNotFound('fs/write_text_file');
            },

 // Terminal — not supported
            createTerminal: async (_params: CreateTerminalRequest): Promise<CreateTerminalResponse> => {
                throw RequestError.methodNotFound('terminal/create');
            },
            terminalOutput: async (_params: TerminalOutputRequest): Promise<TerminalOutputResponse> => {
                throw RequestError.methodNotFound('terminal/output');
            },
            releaseTerminal: async (_params: ReleaseTerminalRequest): Promise<ReleaseTerminalResponse> => {
                throw RequestError.methodNotFound('terminal/release');
            },
            waitForTerminalExit: async (_params: WaitForTerminalExitRequest): Promise<WaitForTerminalExitResponse> => {
                throw RequestError.methodNotFound('terminal/wait_for_exit');
            },
            killTerminal: async (_params: KillTerminalRequest): Promise<KillTerminalResponse> => {
                throw RequestError.methodNotFound('terminal/kill');
            },
        };
    }

 // ─── ACP Protocol (via SDK) ────────────────────────────

    private async initialize(): Promise<void> {
        if (!this.connection) return;

        try {
            const result = await this.connection.initialize({
                protocolVersion: PROTOCOL_VERSION,
                clientCapabilities: {},
            });

            this.agentCapabilities = result?.agentCapabilities || {};
            this.log.info(`[${this.type}] Initialized. Agent capabilities: ${JSON.stringify(this.agentCapabilities)}`);

 // new session create
            await this.createSession();
        } catch (e: any) {
            this.log.error(`[${this.type}] Initialize failed: ${e?.message}`);
            if (!this.errorReason) {
                this.errorReason = 'init_failed';
                this.errorMessage = `ACP handshake failed: ${e?.message}${this.stderrBuffer.length > 0 ? '\n' + this.stderrBuffer.slice(-2).join('\n').slice(0, 200) : ''}`;
            }
            this.currentStatus = 'error';
        }
    }

    private async createSession(): Promise<void> {
        if (!this.connection) return;

        try {
            const result = await this.connection.newSession({
                cwd: this.workingDir,
                mcpServers: [],
            });
            this.sessionId = result?.sessionId || null;
            this.currentStatus = 'idle';
            this.messages = [];

 // DEBUG: session/new response key check
            this.log.info(`[${this.type}] session/new result keys: ${result ? Object.keys(result).join(', ') : 'null'}`);
            if (result?.configOptions) this.log.debug(`[${this.type}] configOptions: ${JSON.stringify(result.configOptions).slice(0, 500)}`);
            if (result?.modes) this.log.debug(`[${this.type}] modes: ${JSON.stringify(result.modes).slice(0, 300)}`);

 // ACP configOptions parsing (model, thought_level etc)
            this.parseConfigOptions(result?.configOptions);

 // ACP modes parsing
            this.parseModes(result?.modes);

 // Legacy: models.currentModelId (some agent compat)
            if (!this.getCurrentSelection('model') && result?.models?.currentModelId) {
                this.setCurrentSelection('model', result.models.currentModelId);
            }

 // ─── Static config fallback (for agents without config/* support) ───
            if (this.configOptions.length === 0 && this.provider.staticConfigOptions?.length) {
                this.useStaticConfig = true;
                for (const sc of this.provider.staticConfigOptions) {
                    const defaultVal = this.selectedConfig[sc.configId] || sc.defaultValue || sc.options[0]?.value;
                    this.configOptions.push({
                        category: sc.category,
                        configId: sc.configId,
                        currentValue: defaultVal,
                        options: sc.options.map(o => ({ ...o })),
                    });
                    if (defaultVal) {
                        this.selectedConfig[sc.configId] = defaultVal;
                        if (sc.category === 'model' || sc.category === 'mode') {
                            this.setCurrentSelection(sc.category, defaultVal);
                        }
                    }
                }
                this.log.info(`[${this.type}] Using static configOptions (${this.configOptions.length} options)`);
            }

            const currentModel = this.getCurrentSelection('model');
            const currentMode = this.getCurrentSelection('mode');
            this.log.info(`[${this.type}] Session created: ${this.sessionId}${currentModel ? ` (model: ${currentModel})` : ''}${currentMode ? ` (mode: ${currentMode})` : ''}`);
            if (this.configOptions.length > 0) {
                this.log.info(`[${this.type}] Config options: ${this.configOptions.map(c => `${c.category}(${c.options.length})`).join(', ')}`);
            }
        } catch (e: any) {
            this.log.warn(`[${this.type}] session/new failed: ${e?.message}`);
            if (!this.errorReason) {
                this.errorReason = 'init_failed';
                this.errorMessage = `ACP session creation failed: ${e?.message}`;
            }
            this.currentStatus = 'error';
        }
    }

    async sendPrompt(text: string, contentBlocks?: ContentBlock[]): Promise<void> {
        if (!this.connection || !this.sessionId) {
            this.log.warn(`[${this.type}] Cannot send prompt: no active connection/session`);
            return;
        }

        if (this._sendPromptInFlight) {
            this.log.warn(`[${this.type}] sendPrompt already in flight — dropping concurrent request`);
            throw new Error('ACP sendPrompt already in flight');
        }
        this._sendPromptInFlight = true;

 // Build prompt content
        const promptParts: any[] = contentBlocks && contentBlocks.length > 0
            ? contentBlocks.map((b) => {
                if (b.type === 'text') return { type: 'text', text: b.text };
                if (b.type === 'image') {
                    return {
                        type: 'image',
                        data: b.data,
                        mimeType: b.mimeType,
                        ...(b.uri ? { uri: b.uri } : {}),
                        ...(b.alt ? { alt: b.alt } : {}),
                    };
                }
                if (b.type === 'audio') {
                    return {
                        type: 'audio',
                        data: b.data,
                        mimeType: b.mimeType,
                        ...(b.uri ? { uri: b.uri } : {}),
                        ...(b.transcript ? { transcript: b.transcript } : {}),
                    };
                }
                if (b.type === 'video') {
                    return b.uri
                        ? {
                            type: 'resource_link',
                            uri: b.uri,
                            name: path.basename(b.uri),
                            mimeType: b.mimeType,
                            ...(b.transcript ? { description: b.transcript } : {}),
                        }
                        : { type: 'text', text: b.transcript || `[Video attachment: ${b.mimeType}]` };
                }
                if (b.type === 'resource_link') {
                    return {
                        type: 'resource_link',
                        uri: b.uri,
                        name: b.name,
                        ...(b.title ? { title: b.title } : {}),
                        ...(b.description ? { description: b.description } : {}),
                        ...(b.mimeType ? { mimeType: b.mimeType } : {}),
                        ...(typeof b.size === 'number' ? { size: b.size } : {}),
                        ...(b.annotations ? { annotations: b.annotations } : {}),
                    };
                }
                if (b.type === 'resource') return { type: 'resource', resource: b.resource };
                return { type: 'text', text: flattenContent([b]) };
            })
            : [{ type: 'text', text }];

 // Add user message locally (store as ContentBlock[])
        this.messages.push(buildUserChatMessage({
            content: contentBlocks && contentBlocks.length > 0 ? contentBlocks : text,
            timestamp: Date.now(),
        }));

        this.currentStatus = 'generating';
        this.partialContent = '';
        this.partialThoughtContent = '';
        this.partialBlocks = [];
        this.turnToolCalls = [];
        this.detectStatusTransition();
        this.log.info(`[${this.type}] Sending prompt: "${text.slice(0, 100)}" (${promptParts.length} parts)`);

        try {
            const result = await this.connection.prompt({
                sessionId: this.sessionId,
                prompt: promptParts,
            });

 // Prompt complete → reflect final message
            if (result?.stopReason) {
                this.stopReason = result.stopReason;
            }
            this.log.info(`[${this.type}] Prompt completed: stopReason=${result?.stopReason} partialContent=${this.partialContent.length} chars partialBlocks=${this.partialBlocks.length}`);

 // Build final assistant message with rich content
            this.finalizeAssistantMessage();

            this.currentStatus = 'idle';
            this.detectStatusTransition();
        } catch (e: any) {
            this.log.warn(`[${this.type}] prompt error: ${e?.message}`);
            this.finalizeAssistantMessage();
            this.currentStatus = 'idle';
            this.detectStatusTransition();
        } finally {
            this._sendPromptInFlight = false;
        }
    }

    private async cancelSession(): Promise<void> {
        if (!this.connection || !this.sessionId) return;

        await this.connection.cancel({
            sessionId: this.sessionId,
        });
        this.currentStatus = 'idle';
        this.detectStatusTransition();
    }

    private permissionResolvers: ((approved: boolean) => void)[] = [];

    // Provider-common manual-attendance signal: while a human is actively driving
    // this session from the dashboard, auto-approve holds so they can decide on
    // the permission request themselves. Background workers are never attended →
    // delegated auto-approve is unaffected.
    private readonly manualAttendance = new ManualAttendanceTracker();

    /** @see ProviderInstance.noteManualInteraction */
    noteManualInteraction(now = Date.now()): void {
        this.manualAttendance.note(now);
    }

    async resolvePermission(approved: boolean): Promise<void> {
        const resolver = this.permissionResolvers.shift();
        if (resolver) {
            resolver(approved);
        }
        if (this.currentStatus === 'waiting_approval') {
            this.currentStatus = 'generating';
            this.detectStatusTransition();
        }
    }

 // ─── ACP session/update handle ─────────────────────

    private handleSessionUpdate(params: SessionNotification): void {
        if (!params) return;

        const update = params.update;
        this.log.debug(`[${this.type}] sessionUpdate: ${update.sessionUpdate}`);

        switch (update.sessionUpdate) {
            case 'agent_message_chunk': {
                const content: any = update.content;
                if (content.type === 'text') {
                    this.partialContent += content.text;
                } else if (content.type === 'image') {
                    this.partialBlocks.push({
                        type: 'image',
                        data: content.data,
                        mimeType: content.mimeType,
                        ...(content.uri ? { uri: content.uri } : {}),
                    });
                } else if (content.type === 'audio') {
                    this.partialBlocks.push({
                        type: 'audio',
                        data: content.data,
                        mimeType: content.mimeType,
                        ...(content.uri ? { uri: content.uri } : {}),
                        ...(content.transcript ? { transcript: content.transcript } : {}),
                    });
                } else if (content.type === 'video') {
                    this.partialBlocks.push({
                        type: 'video',
                        data: content.data,
                        mimeType: content.mimeType,
                        ...(content.uri ? { uri: content.uri } : {}),
                        ...(content.transcript ? { transcript: content.transcript } : {}),
                        ...(content.posterUri ? { posterUri: content.posterUri } : {}),
                    });
                } else if (content.type === 'resource_link') {
                    this.partialBlocks.push({
                        type: 'resource_link',
                        uri: content.uri,
                        name: content.name || 'resource',
                        title: content.title ?? undefined,
                        mimeType: content.mimeType ?? undefined,
                    });
                } else if (content.type === 'resource') {
                    this.partialBlocks.push({
                        type: 'resource',
                        resource: content.resource,
                    });
                }
                this.currentStatus = 'generating';
                break;
            }
            case 'agent_thought_chunk': {
                const content = update.content;
                if (content?.type === 'text' && typeof content.text === 'string') {
                    this.partialThoughtContent += content.text;
                }
                this.currentStatus = 'generating';
                break;
            }
            case 'user_message_chunk': {
                break;
            }
            case 'tool_call': {
 // New tool call — ACP SDK ToolCall has all fields typed
                const tcId = update.toolCallId || `tc_${Date.now()}`;
                const tcTitle = update.title || 'unknown';
                const tcKind = update.kind as ToolKind | undefined;
                const tcStatus = this.mapToolCallStatus(update.status);
                
                this.activeToolCalls.push({
                    id: tcId,
                    name: tcTitle,
                    status: tcStatus,
                    input: update.rawInput ? (typeof update.rawInput === 'string' ? update.rawInput : JSON.stringify(update.rawInput)) : undefined,
                });
                
                // Also collect as ToolCallInfo for rich content
                const acpStatus = update.status || 'in_progress';
                this.turnToolCalls.push({
                    toolCallId: tcId,
                    title: tcTitle,
                    kind: tcKind,
                    status: acpStatus as TCS,
                    rawInput: update.rawInput,
                    content: this.convertToolCallContent(update.content),
                    locations: update.locations,
                });
                break;
            }
            case 'tool_call_update': {
 // Update existing tool call — ACP SDK ToolCallUpdate typed
                const toolCallId = update.toolCallId;
                const existing = this.activeToolCalls.find(t => t.id === toolCallId);
                if (existing) {
                    if (update.status) existing.status = this.mapToolCallStatus(update.status);
                    if (update.rawOutput) existing.output = typeof update.rawOutput === 'string' ? update.rawOutput : JSON.stringify(update.rawOutput);
                }
                // Update ToolCallInfo too
                const tcInfo = this.turnToolCalls.find(t => t.toolCallId === toolCallId);
                if (tcInfo) {
                    if (update.status) tcInfo.status = update.status as TCS;
                    if (update.rawOutput) tcInfo.rawOutput = update.rawOutput;
                    if (update.content) tcInfo.content = this.convertToolCallContent(update.content);
                    if (update.locations) tcInfo.locations = update.locations;
                }
                break;
            }
            case 'current_mode_update': {
                this.setCurrentSelection('mode', update.currentModeId);
                break;
            }
            case 'config_option_update': {
                if (update.configOptions) {
                    this.parseConfigOptions(update.configOptions);
                }
                break;
            }
            case 'plan':
            case 'available_commands_update':
            case 'session_info_update':
            case 'usage_update':
 // Noted but no specific handling needed
                break;
            default:
 // Unknown update type — try legacy parsing for backward compatibility
                this.handleLegacyUpdate(update);
                break;
        }
    }

 /** Handle legacy session/update formats (pre-standardization compat) */
    private handleLegacyUpdate(params: any): void {
 // Legacy: messageDelta format
        if (params.messageDelta) {
            const delta = params.messageDelta;
            if (delta.content) {
                for (const part of Array.isArray(delta.content) ? delta.content : [delta.content]) {
                    if (part.type === 'text' && part.text) {
                        this.partialContent += part.text;
                    }
                }
            }
            this.currentStatus = 'generating';
        }

 // Legacy: message complete
        if (params.message) {
            const m = params.message;
            let content = '';
            if (typeof m.content === 'string') {
                content = m.content;
            } else if (Array.isArray(m.content)) {
                content = m.content
                    .filter((p: any) => p.type === 'text')
                    .map((p: any) => p.text || '')
                    .join('\n');
            }

            if (content.trim()) {
                this.messages.push(buildChatMessage({
                    role: m.role || 'assistant',
                    content: content.trim(),
                    timestamp: Date.now(),
                }));
                this.partialContent = '';
            }
        }

 // Legacy: toolCallUpdate
        if (params.toolCallUpdate) {
            const tc = params.toolCallUpdate;
            const existing = this.activeToolCalls.find(t => t.id === tc.id);
            if (existing) {
                if (tc.status) existing.status = tc.status;
                if (tc.output) existing.output = tc.output;
            } else {
                this.activeToolCalls.push({
                    id: tc.id || `tc_${Date.now()}`,
                    name: tc.name || 'unknown',
                    status: tc.status || 'running',
                    input: typeof tc.input === 'string' ? tc.input : JSON.stringify(tc.input),
                });
            }
        }

 // Legacy: stopReason
        if (params.stopReason) {
            this.stopReason = params.stopReason;
            if (params.stopReason !== 'cancelled') {
                this.currentStatus = 'idle';
            }
            this.activeToolCalls = [];
            this.detectStatusTransition();
        }

 // Legacy: model info
        if (params.model) {
            this.setCurrentSelection('model', params.model);
        }
    }

 /** Map SDK ToolCallStatus to internal status */
    private mapToolCallStatus(status?: ToolCallStatus | string): 'running' | 'completed' | 'failed' {
        switch (status) {
            case 'completed': return 'completed';
            case 'failed': return 'failed';
            case 'pending':
            case 'in_progress':
            default: return 'running';
        }
    }

 // ─── Rich Content Helpers ────────────────────────────

    /** Build ContentBlock[] from current partial state */
    private buildPartialBlocks(): ContentBlock[] {
        const blocks: ContentBlock[] = [];
        if (this.partialContent.trim()) {
            blocks.push({ type: 'text', text: this.partialContent.trim() + '...' });
        }
        blocks.push(...this.partialBlocks);
        return blocks;
    }

    private buildPartialThoughtMessage(timestamp = Date.now()): AcpMessage | null {
        const content = this.partialThoughtContent.trim();
        if (!content) return null;
        return buildThoughtChatMessage({
            content,
            timestamp,
            meta: {
                label: 'Thought',
                isRunning: this.currentStatus === 'generating',
            },
        });
    }

    private buildToolCallBubbleKind(toolCall: ToolCallInfo): 'thought' | 'tool' | 'terminal' {
        if (toolCall.kind === 'think') return 'thought';
        if (toolCall.kind === 'execute') return 'terminal';
        if (Array.isArray(toolCall.content) && toolCall.content.some((entry) => entry?.type === 'terminal')) return 'terminal';
        return 'tool';
    }

    private summarizeToolCallBubbleContent(toolCall: ToolCallInfo): string {
        const rawOutput = typeof toolCall.rawOutput === 'string'
            ? toolCall.rawOutput.trim()
            : (toolCall.rawOutput != null ? JSON.stringify(toolCall.rawOutput) : '');
        if (rawOutput) return rawOutput;

        const contentText = Array.isArray(toolCall.content)
            ? toolCall.content
                .map((entry) => {
                    if (!entry || typeof entry !== 'object') return '';
                    if (entry.type === 'content') return flattenContent([entry.content]).trim();
                    if (entry.type === 'diff') return `${entry.path}\n${entry.newText || ''}`.trim();
                    if (entry.type === 'terminal') return `Terminal: ${entry.terminalId || ''}`.trim();
                    return '';
                })
                .filter(Boolean)
                .join('\n\n')
                .trim()
            : '';
        if (contentText) return contentText;

        const rawInput = typeof toolCall.rawInput === 'string'
            ? toolCall.rawInput.trim()
            : (toolCall.rawInput != null ? JSON.stringify(toolCall.rawInput) : '');
        if (rawInput) {
            return toolCall.title ? `${toolCall.title}\n${rawInput}` : rawInput;
        }

        return toolCall.title || '';
    }

    private buildTurnToolCallMessages(timestamp = Date.now()): AcpMessage[] {
        return this.turnToolCalls
            .map((toolCall) => {
                const content = this.summarizeToolCallBubbleContent(toolCall);
                if (!content) return null;
                const isRunning = toolCall.status === 'pending' || toolCall.status === 'in_progress';
                const label = toolCall.title || undefined;
                const kind = this.buildToolCallBubbleKind(toolCall);
                if (kind === 'thought') {
                    return buildThoughtChatMessage({
                        content,
                        timestamp,
                        meta: { label: label || 'Thought', isRunning },
                    });
                }
                if (kind === 'terminal') {
                    return buildTerminalChatMessage({
                        content,
                        timestamp,
                        meta: { label: label || 'Ran command', isRunning },
                    });
                }
                return buildToolChatMessage({
                    content,
                    timestamp,
                    meta: { label: label || 'Tool call', isRunning },
                });
            })
            .filter(Boolean) as AcpMessage[];
    }

    /** Finalize streaming content into an assistant message */
    private finalizeAssistantMessage(): void {
        const timestamp = Date.now();
        const thoughtMessage = this.buildPartialThoughtMessage(timestamp);
        if (thoughtMessage) {
            this.messages.push(thoughtMessage);
        }

        const toolCallMessages = this.buildTurnToolCallMessages(timestamp);
        if (toolCallMessages.length > 0) {
            this.messages.push(...toolCallMessages);
        }

        const blocks = this.buildPartialBlocks();
        // Remove trailing '...' from text blocks for final message
        const finalBlocks = blocks.map(b => {
            if (b.type === 'text' && b.text.endsWith('...')) {
                return { ...b, text: b.text.slice(0, -3) };
            }
            return b;
        }).filter(b => b.type !== 'text' || (b.type === 'text' && b.text.trim()));

        if (finalBlocks.length > 0) {
            this.messages.push(buildAssistantChatMessage({
                content: finalBlocks.length === 1 && finalBlocks[0].type === 'text'
                    ? (finalBlocks[0] as {type: 'text', text: string}).text   // single text → string (backward compat)
                    : finalBlocks,
                timestamp: Date.now(),
                toolCalls: this.turnToolCalls.length > 0 ? [...this.turnToolCalls] : undefined,
            }));
        }
        this.partialContent = '';
        this.partialThoughtContent = '';
        this.partialBlocks = [];
        this.turnToolCalls = [];
    }

    /** Convert ACP ToolCallContent[] to our ToolCallContent[] */
    private convertToolCallContent(acpContent?: any[]): TCC[] | undefined {
        if (!acpContent || !Array.isArray(acpContent)) return undefined;
        return acpContent.map((c: any) => {
            if (c.type === 'diff') {
                return { type: 'diff' as const, path: c.path || '', oldText: c.oldText, newText: c.newText || '' };
            }
            if (c.type === 'terminal') {
                return { type: 'terminal' as const, terminalId: c.terminalId || '' };
            }
            // type: 'content' or unknown
            return { type: 'content' as const, content: c.content || { type: 'text' as const, text: JSON.stringify(c) } };
        });
    }

 // ─── status transition detect ────────────────────────────

    private detectStatusTransition(): void {
        const now = Date.now();
        const newStatus = this.currentStatus;
        const dirName = workingDirBasename(this.workingDir);
        const chatTitle = `${this.provider.name} · ${dirName}`;
        const progressFingerprint = newStatus === 'generating'
            ? `${this.partialContent}::${JSON.stringify(this.partialBlocks)}::${JSON.stringify(this.activeToolCalls.map(t => ({ name: t.name, status: t.status })))}`.slice(-2000)
            : undefined;

        if (newStatus !== this.lastStatus) {
            if (this.lastStatus === 'idle' && newStatus === 'generating') {
                this.generatingStartedAt = now;
                this.pushEvent({ event: 'agent:generating_started', chatTitle, timestamp: now });
            } else if (newStatus === 'waiting_approval') {
                if (!this.generatingStartedAt) this.generatingStartedAt = now;
                this.pushEvent({
                    event: 'agent:waiting_approval', chatTitle, timestamp: now,
                    modalMessage: this.activeToolCalls.find(t => t.status === 'running')?.name,
                });
            } else if (newStatus === 'idle' && (this.lastStatus === 'generating' || this.lastStatus === 'waiting_approval')) {
                const duration = this.generatingStartedAt ? Math.round((now - this.generatingStartedAt) / 1000) : 0;
                this.pushEvent({ event: 'agent:generating_completed', chatTitle, duration, timestamp: now, finalSummary: extractFinalSummaryFromMessages(this.messages) });
                this.generatingStartedAt = 0;
            } else if (newStatus === 'stopped') {
                this.pushEvent({ event: 'agent:stopped', chatTitle, timestamp: now });
            }
            this.lastStatus = newStatus;
        }

 // Monitor check
        const agentKey = `${this.type}:acp`;
        const approvalPending = newStatus === 'waiting_approval';
        const monitorEvents = this.monitor.check(agentKey, newStatus, now, progressFingerprint, approvalPending);
        for (const me of monitorEvents) {
            this.pushEvent({ event: me.type, agentKey: me.agentKey, message: me.message, elapsedSec: me.elapsedSec, timestamp: me.timestamp });
        }
    }

    private pushEvent(event: ProviderEvent): void {
        this.events.push(event);
    }

    private appendSystemMessage(content: string, timestamp = Date.now()): void {
        const normalizedContent = String(content || '').trim();
        if (!normalizedContent) return;
        this.messages.push(buildRuntimeSystemChatMessage({
            content: normalizedContent,
            timestamp,
        }));
        if (this.messages.length > 200) {
            this.messages = this.messages.slice(-100);
        }
    }

    private flushEvents(): ProviderEvent[] {
        const events = [...this.events];
        this.events = [];
        return events;
    }

 // ─── external access ─────────────────────────────────

    get cliType(): string { return this.type; }
    get cliName(): string { return this.provider.name; }

 /** ACP Agent capabilities (available after initialize) */
    getCapabilities(): Record<string, any> { return this.agentCapabilities; }
}
